import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import RadioButtonUncheckedOutlined from "@mui/icons-material/RadioButtonUncheckedOutlined";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { RouterLink } from "@/components/RouterLink";
import { Typography } from "@/components/Typography";
import { useOrgChannels } from "@/features/channels/useOrgChannels";
import { trackGaEvent } from "@/features/ga/trackGaEvent";
import { interlude } from "@/interlude";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px 24px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "8px",
  },
  progress: {
    height: "6px",
    borderRadius: "999px",
    backgroundColor: theme.colors.neutral020,
  },
  list: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))",
    gap: "8px 24px",
    margin: "0px",
    padding: "0px",
    listStyle: "none",
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "8px 0px",
  },
  doneIcon: {
    fontSize: "1.25rem",
    color: theme.colors.staticFgPositive,
  },
  todoIcon: {
    fontSize: "1.25rem",
    color: theme.colors.neutral050,
  },
  text: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface CompletenessItem {
  key: string;
  label: string;
  benefit: string;
  done: boolean;
  to: "/channels" | "/applications" | "/data-hub";
}

/**
 * How much of the org's customer data is connected, as a checklist of the
 * sources most brands use. Each item is judged from data the platform has;
 * the card waits until all of it has loaded so no item reads as missing
 * while it is still loading.
 */
export const DataCompleteness: React.FC = function DataCompleteness() {
  const { t } = useTranslation();
  const orgChannels = useOrgChannels();
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();
  const hasEcModuleQuery = interlude.organization.useHasEcModule();

  if (!orgChannels.isSuccess || !unifyScopeSettingQuery.isSuccess) return null;

  const types = new Set(orgChannels.channels.map((channel) => channel.type));
  const ecPlatforms = hasEcModuleQuery.data?.ecPlatforms;
  const items: Array<CompletenessItem> = [
    {
      key: "line",
      label: "LINE",
      benefit: t(
        "dataHub.flow.missing.line",
        "Every LINE friend becomes a customer profile",
      ),
      done: types.has("line"),
      to: "/channels",
    },
    {
      key: "social",
      label: "Facebook / Instagram",
      benefit: t(
        "overview.completeness.social",
        "People who comment or message join the same profile",
      ),
      done: types.has("fb") || types.has("ig"),
      to: "/channels",
    },
    {
      key: "web",
      label: t("overview.completeness.web", "Website"),
      benefit: t(
        "dataHub.flow.missing.web",
        "See which products customers browse on your website",
      ),
      done: types.has("wccs"),
      to: "/channels",
    },
    // Left out when an older interlude response has no platform list.
    ...(ecPlatforms
      ? [
          {
            key: "ecommerce",
            label: t("dataHub.sources.ecommerce.title", "E-commerce platforms"),
            benefit: t(
              "dataHub.flow.missing.ecommerce",
              "Bring purchase totals into each customer's profile",
            ),
            done: ecPlatforms.length > 0,
            to: "/applications" as const,
          },
        ]
      : []),
    {
      key: "unification",
      label: t("dataHub.page.title", "Profile unification"),
      benefit: t(
        "overview.completeness.unification",
        "Merge every channel into one profile per customer",
      ),
      done: unifyScopeSettingQuery.data.isCdhConnected,
      to: "/data-hub",
    },
  ];
  const doneCount = items.filter((item) => item.done).length;

  return (
    <Box component="section" sx={styles.root}>
      <Box sx={styles.header}>
        <Typography variant="h3" fontWeight={700}>
          {t("overview.completeness.title", "Your customer data")}
        </Typography>
        <Typography variant="note" color={theme.colors.staticFgNote}>
          {t("overview.completeness.progress", {
            done: doneCount,
            total: items.length,
            defaultValue: "{{done}} of {{total}} connected",
          })}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={(doneCount / items.length) * 100}
        sx={styles.progress}
        aria-label={t("overview.completeness.title", "Your customer data")}
      />
      <Box component="ul" sx={styles.list}>
        {items.map((item) => (
          <Box component="li" key={item.key} sx={styles.item}>
            {item.done ? (
              <CheckCircleOutlined sx={styles.doneIcon} />
            ) : (
              <RadioButtonUncheckedOutlined sx={styles.todoIcon} />
            )}
            <Box sx={styles.text}>
              <Typography variant="body" fontWeight={500}>
                {item.label}
              </Typography>
              {item.done ? null : (
                <>
                  <Typography variant="note" color={theme.colors.staticFgNote}>
                    {item.benefit}
                  </Typography>
                  <RouterLink
                    to={item.to}
                    variant="body2"
                    onClick={() =>
                      trackGaEvent("overviewCompletenessClick", {
                        label: item.key,
                      })
                    }
                  >
                    {t("dataHub.flow.missing.connect", "Connect")}
                  </RouterLink>
                </>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
